import { useEffect } from 'react';

import type { ProjectBackgroundEntry } from './projectsBackgroundRegistry';

interface UseProjectBackgroundRegistrationArgs {
  projectId: string;
  projectTitle: string;
  agentRunId?: string;
  runFailed: boolean;
  runClosed: boolean;
  desktopTargetEnabled?: boolean;
  enabled?: boolean;
  /**
   * A note-push project has a patient bound; that fact (never the name) is
   * persisted so a restart restore can fail closed instead of attaching
   * unredacted. The caller reads it from the copilot run context.
   */
  requiresRedactions: boolean;
  /** The app's one registry, bound to the desktop poke where it is created. */
  registerProjectBackground: (entry: Omit<ProjectBackgroundEntry, 'updatedAt'>) => void;
  unregisterProjectBackground: (agentRunId: string) => void;
}

export const useProjectBackgroundRegistration = ({
  projectId,
  projectTitle,
  agentRunId,
  runFailed,
  runClosed,
  desktopTargetEnabled,
  enabled = true,
  requiresRedactions,
  registerProjectBackground,
  unregisterProjectBackground,
}: UseProjectBackgroundRegistrationArgs) => {
  useEffect(() => {
    if (!agentRunId) return;
    // The registry stays alive across navigation: deregistration is terminal
    // run status, not unmount. A cleanup here would drop an owner's desktop
    // session when they leave the chat. A locked viewer never registers, and
    // drops a session claimed before the grant resolved.
    if (!enabled) {
      unregisterProjectBackground(agentRunId);
      return;
    }
    registerProjectBackground({
      projectId,
      projectTitle,
      agentRunId,
      origin: 'main',
      requiresRedactions,
      desktopTargetEnabled,
    });
  }, [
    enabled,
    agentRunId,
    projectId,
    projectTitle,
    requiresRedactions,
    desktopTargetEnabled,
    registerProjectBackground,
    unregisterProjectBackground,
  ]);

  useEffect(() => {
    if (agentRunId && (runFailed || runClosed)) {
      unregisterProjectBackground(agentRunId);
    }
  }, [agentRunId, runFailed, runClosed, unregisterProjectBackground]);
};
